/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2024 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'preact/compat';
import { Container, Slot, SlotProps } from '@adobe-commerce/elsie/lib';
import { ProductModel } from '@/pdp/data/models';
import { events } from '@adobe-commerce/event-bus';
import { Swatches } from '@/pdp/components';
import {
  setProductConfigurationValues,
  getProductConfigurationValues,
  setProductConfigurationValid,
  fetchProductData,
  getFetchedProductData,
  config,
  OptionsTransformer,
} from '@/pdp/api';
import { setUrlParams } from '@/pdp/lib/url-params';
import { publishProductPageViewEvent } from '@/pdp/lib/acdl';
import {
  ImageNodeRenderProps,
  ImageProps,
} from '@adobe-commerce/elsie/components';

/** @deprecated Use OptionsTransformer from @/pdp/api instead */
export type ProductOptionsTransformer = OptionsTransformer;

export interface ProductOptionsProps extends HTMLAttributes<HTMLDivElement> {
  scope?: string;
  hideSelectedValue?: boolean;
  onValues?: (optionsUIDs: string[]) => void;
  onErrors?: (errors: { [id: string]: string }) => void;
  transformer?: ProductOptionsTransformer;
  slots?: {
    Swatches?: SlotProps<{ data: ProductModel | null; optionsUIDs: string[] }>;
    SwatchImage?: SlotProps<{
      data: ProductModel | null;
      optionsUIDs: string[];
      imageSwatchContext: ImageNodeRenderProps['imageSwatchContext'];
      defaultImageProps: ImageProps;
    }>;
  };
}

export const ProductOptions: Container<
  ProductOptionsProps,
  { data: ProductModel | null; optionsUIDs: string[] }
> = ({
  initialData = { data: null, optionsUIDs: [] },
  scope,
  hideSelectedValue = false,
  transformer,
  slots,
  onValues,
  onErrors,
  children,
  ...props
}) => {
  const [data, setData] = useState<ProductModel | null>(initialData.data);

  const [current, setCurrent] = useState<string[]>(initialData.optionsUIDs);

  const [selectionsToUpdate, setSelectionsToUpdate] =
    useState<ProductModel['options']>();

  useEffect(() => {
    const dataEvent = events.on('pdp/data', setData, { scope });

    const valuesEvent = events.on(
      'pdp/values',
      ({ optionsUIDs }) => {
        if (optionsUIDs) {
          setCurrent(optionsUIDs);
        }
      },
      { scope }
    );

    return () => {
      dataEvent?.off();
      valuesEvent?.off();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOptionSelected = useCallback(
    async (selections: { [id: string]: { label: string; value: string } }) => {
      if (!data?.sku) return;

      const { persistURLParams, anchors, acdl } = config.getConfig() ?? {};

      // options selected
      let optionsUIDs = Object.keys(selections)
        .map((id) => selections[id].value)
        .filter((element) => element !== undefined);

      // set optionUIDs to URL params
      // ?optionsUIDs=1,2,3
      // only set the URL params if the "persistURLParams" is set to true and the product is not a bundle type
      if (persistURLParams && !data?.isBundle) {
        setUrlParams({ optionsUIDs: optionsUIDs.join(',') });
      }

      // check if the options have changed and update the values only if they have changed
      if (
        getProductConfigurationValues({ scope })?.optionsUIDs?.join('|') !==
        optionsUIDs?.join('|')
      ) {
        // update values
        setProductConfigurationValues((prev) => ({ ...prev, optionsUIDs }), {
          scope,
        });
      }

      // pass the selected options to callback
      onValues?.(optionsUIDs);

      // refetch data
      const next = await fetchProductData(data.sku, {
        optionsUIDs: optionsUIDs ?? [],
        isBundle: data?.isBundle,
        anchors,
      });

      // emit the new data
      events.emit('pdp/data', next, { scope });

      let valid = optionsUIDs?.length === next?.options?.length;

      // special processing for anchor options (options that should not be refined when other options are selected)
      if (anchors?.length) {
        const updatedOptionsUIDs = next?.optionUIDs ?? [];

        const isMatching = optionsUIDs.every((item) =>
          updatedOptionsUIDs.includes(item)
        );

        if (!isMatching) {
          setSelectionsToUpdate(next?.options);
          setProductConfigurationValues(
            (prev) => ({
              ...prev,
              optionsUIDs: updatedOptionsUIDs,
            }),
            { scope }
          );

          valid = updatedOptionsUIDs?.length === next?.options?.length;
        }
      }

      // is the product valid
      setProductConfigurationValid(() => valid, { scope });

      // ACDL
      if (acdl && next && next?.variantSku !== data.sku) {
        publishProductPageViewEvent(next);
      }
    },
    [data?.isBundle, data?.sku, onValues, scope]
  );

  const handleErrors = useCallback(
    (errors: { [id: string]: string }) => {
      const hasErrors = Object.keys(errors).length !== 0;

      if (hasErrors) {
        setProductConfigurationValid(() => false, { scope });
        onErrors?.(errors);
      }
    },
    [onErrors, scope]
  );

  // Apply transformer to options if provided
  const transformedOptions = useMemo(() => {
    if (!data?.options) return undefined;

    if (!transformer) {
      return data.options;
    }

    try {
      const transformed = transformer(data.options);
      // Fallback to original options if transformer returns null/undefined
      return transformed ?? data.options;
    } catch (error) {
      console.error(
        'ProductOptions: transformer threw an error, using original options',
        error
      );
      return data.options;
    }
  }, [data?.options, transformer]);

  if (!data) return null;

  // Only show if there are options in the data
  if (!transformedOptions) return null;

  return (
    <Slot
      name="Swatches"
      slot={slots?.Swatches}
      context={{ data, optionsUIDs: current }}
    >
      <Swatches
        {...props}
        options={transformedOptions}
        selectedUIDs={current}
        defaultOptions={current}
        selectionsToUpdate={selectionsToUpdate}
        hideSelectedValue={hideSelectedValue}
        disablePreselections={!config.getConfig()?.preselectFirstOption}
        onValues={handleOptionSelected}
        onErrors={handleErrors}
        imageSwatchNode={
          slots?.SwatchImage
            ? ({ imageSwatchContext, ...imageProps }) => (
                <Slot
                  name="SwatchImage"
                  slot={slots?.SwatchImage}
                  slotTag="span"
                  contentTag="span"
                  context={{
                    data,
                    optionsUIDs: current,
                    defaultImageProps: imageProps,
                    imageSwatchContext,
                  }}
                />
              )
            : undefined
        }
      />
    </Slot>
  );
};

ProductOptions.getInitialData = async function (options) {
  const data = await getFetchedProductData({ scope: options?.scope });
  const optionsUIDs =
    getProductConfigurationValues({ scope: options?.scope })?.optionsUIDs ?? [];
  return { data, optionsUIDs };
};
